import { AttachmentDto } from '@brandfolder-panel/sdk';
import { useContext } from 'react';
import { toast } from 'react-hot-toast';

import { AsideContext } from '@context/aside';
import { BlockContext } from '@context/blocks';
import { LocaleContext } from '@context/locale';
import { PageContext } from '@context/page';
import { PublishContext } from '@context/publish';
import { BlockTypes } from '@enums/blocks';
import { Attachment } from '@typings/attachment';
import { getAttachment, getIsAttachmentUrlSmartCdn } from '@utilities/attachment';
import {
  createBlock,
  createBlockDocData,
  createBlockImageData,
  createBlockPdfData,
  createBlockVideoData,
  insertBlock
} from '@utilities/block';

export function usePanelSdkAttachmentHandlers() {
  const { setShowBlocks } = useContext(AsideContext);
  const { blocks, setBlocks } = useContext(BlockContext);
  const { locale } = useContext(LocaleContext);
  const { page } = useContext(PageContext);
  const { setPublish } = useContext(PublishContext);

  const addAttachmentBlock = async (
    dto: AttachmentDto,
    parentKey: string | undefined,
    position: number
  ): Promise<void> => {
    setShowBlocks(false);

    if (getIsAttachmentUrlSmartCdn(dto.url)) {
      setAttachment(dto, parentKey, position);
    } else {
      const apiDatum = await getAttachment(dto.id);
      if (apiDatum) {
        setAttachment(dto, parentKey, position, apiDatum.data.attributes);
      } else {
        displayBlockErrorToast();
      }
    }
  };

  const setAttachment = (
    dto: AttachmentDto,
    parentKey: string | undefined,
    position: number,
    attachment?: Attachment
  ) => {
    const blockType = getAttachmentBlockType(dto);
    if (blockType) {
      const block = createBlock({
        data: createBlockData(dto, attachment),
        locale,
        pageKey: page?.key as string,
        parentKey,
        position,
        type: blockType
      });

      setBlocks([...insertBlock(block, blocks, page?.key as string)]);
      setPublish(true);
    } else {
      displayBlockErrorToast();
    }
  };

  return {
    addAttachmentBlock
  };
}

const createBlockData = (dto: AttachmentDto, attachment?: Attachment) => {
  switch (getAttachmentBlockType(dto)) {
    case BlockTypes.Image: {
      return createBlockImageData(dto, attachment);
    }
    case BlockTypes.Video: {
      return createBlockVideoData(dto, attachment);
    }
    case BlockTypes.Word:
    case BlockTypes.PowerPoint:
    case BlockTypes.Excel: {
      return createBlockDocData(dto, attachment);
    }
    case BlockTypes.Pdf: {
      return createBlockPdfData(dto, attachment);
    }
  }
};

const getAttachmentBlockType = (dto: AttachmentDto): BlockTypes | undefined => {
  switch (dto.mimetype.split('/')[0]) {
    case 'image': {
      return BlockTypes.Image;
    }
    case 'video': {
      return BlockTypes.Video;
    }
    case 'application': {
      if (['doc', 'docx'].includes(dto.extension)) return BlockTypes.Word;
      else if (['ppt', 'pptx'].includes(dto.extension)) return BlockTypes.PowerPoint;
      else if (['xls', 'xlsx'].includes(dto.extension)) return BlockTypes.Excel;
      else if (dto.extension === 'pdf') return BlockTypes.Pdf;
      break;
    }
  }
};

function displayBlockErrorToast() {
  toast.error('Error saving block. Please try again.');
}
